import {
  createUnifiedTheme,
  createBaseThemeOptions,
  palettes,
  genPageTheme,
  shapes,
  UnifiedTheme,
} from '@backstage/theme';

// Zambian flag-inspired palette.
//   Green   #198A00  - sovereignty, prosperity (primary)
//   Orange  #EF7D00  - mineral wealth (secondary / accent)
//   Red     #DE2010  - struggle for freedom (error / alerts)
//   Black   #000000  - the people of Zambia (ink)
//
// Text-on-white shades are darkened to meet WCAG 2.1 AA contrast.
export const zambianColors = {
  green: '#198A00',
  greenDark: '#0F5A00',
  greenDeep: '#073800',
  orange: '#EF7D00',
  orangeDark: '#C75F00',
  red: '#DE2010',
  redDark: '#A8160A',
  black: '#000000',
  ink: '#161A14',
  surface: '#FFFFFF',
  surfaceAlt: '#F5F7F4',
  surfaceDark: '#0E120D',
  surfaceDarkAlt: '#161A14',
};

const heroGradient = `linear-gradient(135deg, ${zambianColors.green} 0%, ${zambianColors.greenDeep} 60%, ${zambianColors.black} 100%)`;

export const zambianLightTheme: UnifiedTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: zambianColors.greenDark,
        light: zambianColors.green,
        dark: zambianColors.greenDeep,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: zambianColors.orangeDark,
        light: zambianColors.orange,
        dark: '#8A4200',
        contrastText: '#FFFFFF',
      },
      error: {
        main: zambianColors.redDark,
        light: zambianColors.red,
        dark: '#7A0F06',
        contrastText: '#FFFFFF',
      },
      background: {
        default: zambianColors.surfaceAlt,
        paper: zambianColors.surface,
      },
      banner: {
        info: zambianColors.greenDark,
        error: zambianColors.redDark,
        text: zambianColors.ink,
        link: zambianColors.greenDark,
        closeButtonColor: '#FFFFFF',
        warning: zambianColors.orangeDark,
      },
      navigation: {
        background: zambianColors.surfaceDark,
        indicator: zambianColors.orange,
        color: '#E6EFE2',
        selectedColor: '#FFFFFF',
        navItem: {
          hoverBackground: '#1F2A1A',
        },
        submenu: {
          background: zambianColors.surfaceDarkAlt,
        },
      },
    },
  }),
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [
        zambianColors.green,
        zambianColors.greenDeep,
        zambianColors.black,
      ],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [zambianColors.greenDark, zambianColors.orangeDark],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [zambianColors.orangeDark, zambianColors.greenDark],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [zambianColors.greenDark, zambianColors.greenDeep],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [zambianColors.green, zambianColors.orangeDark],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [zambianColors.greenDark, zambianColors.black],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [zambianColors.greenDark, zambianColors.orangeDark],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [zambianColors.greenDark, zambianColors.orange],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [zambianColors.orangeDark, zambianColors.greenDeep],
      shape: shapes.wave,
    }),
  },
});

export const zambianDarkTheme: UnifiedTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      primary: {
        main: zambianColors.green,
        light: '#3FB820',
        dark: zambianColors.greenDark,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: zambianColors.orange,
        light: '#FFA040',
        dark: zambianColors.orangeDark,
        contrastText: '#000000',
      },
      error: {
        main: zambianColors.red,
        light: '#FF5443',
        dark: zambianColors.redDark,
        contrastText: '#FFFFFF',
      },
      background: {
        default: zambianColors.surfaceDark,
        paper: zambianColors.surfaceDarkAlt,
      },
      banner: {
        info: zambianColors.green,
        error: zambianColors.red,
        text: '#FFFFFF',
        link: zambianColors.orange,
        closeButtonColor: '#FFFFFF',
        warning: zambianColors.orange,
      },
      navigation: {
        background: '#050805',
        indicator: zambianColors.orange,
        color: '#C8D4C2',
        selectedColor: '#FFFFFF',
        navItem: {
          hoverBackground: '#101410',
        },
        submenu: {
          background: '#0A0E0A',
        },
      },
    },
  }),
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [zambianColors.greenDeep, zambianColors.black],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [zambianColors.greenDark, zambianColors.orangeDark],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [zambianColors.orangeDark, zambianColors.greenDeep],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [zambianColors.greenDark, zambianColors.black],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [zambianColors.greenDeep, zambianColors.orangeDark],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [zambianColors.greenDark, zambianColors.black],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [zambianColors.greenDark, zambianColors.orangeDark],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [zambianColors.greenDeep, zambianColors.orange],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [zambianColors.orangeDark, zambianColors.greenDeep],
      shape: shapes.wave,
    }),
  },
});

export { heroGradient };
